import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import useQuery from 'hooks/useQuery';
import { ALL_ACCESS, PREMIUM, FREE } from 'utils/constants';

const FREE_MEMBERSHIP_GRAIN = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.68" numOctaves="3" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(#n)" opacity="0.2"/></svg>'
)}")`;

const CardWrapper = styled.div`
  position: relative;
  border-radius: 16px;
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
`;

const CardInner = styled.div`
  border-radius: 14px;
  overflow: hidden;
  border: ${({ isAllAccess }) =>
    isAllAccess
      ? '1.5px solid rgba(120, 210, 230, 0.75)'
      : '1.5px solid rgba(255, 255, 255, 0.42)'};
  ${({ isAllAccess }) =>
    isAllAccess
      ? `
    background: linear-gradient(
    180deg,
    rgba(24, 80, 108, 0.52) 0%,
    rgba(38, 86, 110, 0.62) 42%,
    rgba(38, 75, 96, 0.55) 100%
  );
  `
      : `
    background-image: ${FREE_MEMBERSHIP_GRAIN},
      linear-gradient(
        90deg,
        rgba(81, 78, 96, 0.78) 0%,
        rgba(77, 74, 93, 0.76) 45%,
        rgba(62, 59, 80, 0.78) 78%,
        rgba(51, 48, 69, 0.82) 100%
      );
    background-size: 140px 140px, 100% 100%;
    background-repeat: repeat, no-repeat;
    background-blend-mode: soft-light, normal;
  `}
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 24px;
  }
`;

const BenefitsList = styled.div`
  flex: 1;
  min-width: 0;
`;

const PricingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (min-width: 768px) {
    align-items: flex-end;
    min-width: 200px;
  }
`;

const PricingBox = styled.div`
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  padding: 12px 20px;
  text-align: left;
  min-width: 180px;
  background: rgba(255, 255, 255, 0.03);
`;

const PlanLabel = styled.div`
  display: inline-block;
  padding: 4px 14px;
  border-radius: 16px;
  background: linear-gradient(135deg, #8372e6 0%, #47bad4 100%);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
`;

const SignUpButton = styled.button`
  width: 100%;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #8372e6 0%, #47bad4 100%);
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;

  &:hover {
    opacity: 0.85;
    transform: translateY(-1px);
  }
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: -14px;
  left: 24px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 20px;
  background: linear-gradient(135deg, #8372e6 0%, #47bad4 100%);
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  z-index: 2;
`;

const CheckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    style={{ flexShrink: 0, marginTop: 2 }}
  >
    <circle cx="10" cy="10" r="9" stroke="#47bad4" strokeWidth="1.5" />
    <path
      d="M6 10l3 3 5-5"
      stroke="#47bad4"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="#ffffff"
    style={{ flexShrink: 0 }}
  >
    <path d="M8 0l2.35 4.76L16 5.53l-4 3.9.94 5.5L8 12.42l-4.94 2.5.94-5.5-4-3.9 5.65-.77z" />
  </svg>
);

const MembershipCard = ({
  memberTier,
  monthlyPrice,
  annualPrice,
  benefits,
  isAllAccessEligible,
  showNextScreen,
}) => {
  const navigate = useNavigate();
  const { memberTier: memberTierQuery } = useQuery();

  const isAllAccess = memberTier === ALL_ACCESS;
  const isFree = memberTier === FREE;
  const showRecommended = isAllAccessEligible
    ? isAllAccess
    : memberTier === PREMIUM;

  const handleSignUp = (billingCycle) => {
    let search = `?memberTier=${memberTier}`;
    if (billingCycle) {
      search += `&billingCycle=${billingCycle}`;
    }
    navigate({
      pathname: '/signup',
      search,
    });
    if (isFree) {
      showNextScreen(2);
    } else {
      showNextScreen();
    }
  };

  const annualMonthlyPrice =
    annualPrice > 0 ? (annualPrice / 12).toFixed(2) : 0;

  const renderPricing = () => {
    if (isFree) {
      return (
        <PricingBox>
          <div
            style={{
              fontSize: 36,
              fontWeight: 700,
              color: '#ffffff',
              lineHeight: 1.2,
              textAlign: 'left',
            }}
          >
            $0
          </div>
          <SignUpButton
            onClick={() => handleSignUp(null)}
            style={{ marginTop: 12 }}
          >
            Sign up for Gig Life Pro
          </SignUpButton>
        </PricingBox>
      );
    }

    if (isAllAccess) {
      return (
        <>
          <PricingBox>
            <PlanLabel>Annual Plan</PlanLabel>
            <div
              style={{
                fontSize: 32,
                fontWeight: 700,
                color: '#ffffff',
                lineHeight: 1.2,
                marginTop: 4,
              }}
            >
              ${annualMonthlyPrice}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: 'rgba(255,255,255,0.6)',
                }}
              >
                {' '}
                USD/month
              </span>
            </div>
            <div
              style={{
                fontSize: 12,
                fontStyle: 'italic',
                color: 'rgba(255,255,255,0.5)',
                marginTop: 4,
              }}
            >
              Billed Annually (USD {annualPrice}/year)
            </div>
            <div
              style={{
                fontSize: 13,
                color: '#47bad4',
                fontWeight: 600,
                marginTop: 4,
              }}
            >
              SAVE USD{' '}
              {(monthlyPrice * 12 - annualPrice).toFixed(0)}
              /YEAR
            </div>
            <SignUpButton
              onClick={() => handleSignUp('year')}
              style={{ marginTop: 12 }}
            >
              Sign up for Gig Life Pro
            </SignUpButton>
          </PricingBox>
          <PricingBox>
            <PlanLabel>Monthly Plan</PlanLabel>
            <div
              style={{
                fontSize: 32,
                fontWeight: 700,
                color: '#ffffff',
                lineHeight: 1.2,
                marginTop: 4,
              }}
            >
              ${monthlyPrice.toFixed(2)}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: 'rgba(255,255,255,0.6)',
                }}
              >
                {' '}
                USD/month
              </span>
            </div>
            <div
              style={{
                fontSize: 12,
                fontStyle: 'italic',
                color: 'rgba(255,255,255,0.5)',
                marginTop: 4,
              }}
            >
              Billed Monthly
            </div>
            <SignUpButton
              onClick={() => handleSignUp('month')}
              style={{ marginTop: 12 }}
            >
              Sign up for Gig Life Pro
            </SignUpButton>
          </PricingBox>
        </>
      );
    }

    return null;
  };

  const renderBenefitItem = (item, index) => {
    const text = typeof item === 'object' ? item.text : item;
    const isBold = typeof item === 'object' && item.bold;

    return (
      <div
        key={index}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginBottom: 12,
        }}
      >
        <CheckIcon />
        <span
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            color: 'rgba(255, 255, 255, 0.85)',
            fontWeight: isBold ? 700 : 400,
          }}
        >
          {text}
        </span>
      </div>
    );
  };

  const cardTitle = isAllAccess
    ? 'All Access Membership'
    : 'Free membership';

  return (
    <CardWrapper isAllAccess={isAllAccess}>
      {showRecommended && (
        <RecommendedBadge>
          <StarIcon />
          RECOMMENDED
        </RecommendedBadge>
      )}
      <CardInner isAllAccess={isAllAccess}>
        <BenefitsList>
          <h3
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: '#ffffff',
              marginBottom: 20,
              lineHeight: 1.3,
            }}
          >
            {cardTitle}
          </h3>
          <div>
            {benefits &&
              benefits.map((item, index) =>
                renderBenefitItem(item, index)
              )}
          </div>
        </BenefitsList>
        <PricingSection>{renderPricing()}</PricingSection>
      </CardInner>
    </CardWrapper>
  );
};

MembershipCard.propTypes = {
  benefits: PropTypes.array.isRequired,
  memberTier: PropTypes.string.isRequired,
  isAllAccessEligible: PropTypes.bool,
  monthlyPrice: PropTypes.number,
  annualPrice: PropTypes.number,
  showNextScreen: PropTypes.func,
};

MembershipCard.defaultProps = {
  monthlyPrice: 0,
  annualPrice: 0,
  isAllAccessEligible: false,
  showNextScreen: () => {},
};

export default MembershipCard;
